import { useState, useRef, useEffect } from 'react';
import { Sigma, ChevronDown } from 'lucide-react';
import { useT } from '@/store/i18n';
import { cn } from '@/lib/cn';

/**
 * Math/symbol picker for question authoring.
 * Tracks the last-focused <input>/<textarea> in the document and inserts the
 * chosen symbol at the caret position via the native value setter so React's
 * onChange handlers fire correctly.
 */

const QUICK = ['+', '−', '×', '÷', '=', '≠', '≤', '≥', '²', '³', '√', '½', '¼', 'π', '°', '□', '○', '△', '→'];

const GROUP_SYMBOLS = [
  ['+', '−', '×', '÷', '=', '≠', '<', '>', '≤', '≥', '±', '·', '∙', '÷', '⁄', '%', '‰'],
  ['½', '⅓', '¼', '⅕', '⅙', '⅛', '⅔', '⅖', '¾', '⅗', '⅘', '⅜', '⅝', '⅞', '⁄'],
  ['¹', '²', '³', '⁴', '⁵', '⁶', '⁷', '⁸', '⁹', '⁰', '⁺', '⁻', '₁', '₂', '₃', '₄', '₅', 'ⁿ'],
  ['√', '∛', '∜', 'π', '∞', '∑', '∏', '∫', 'Δ', '∇', '∂', '∝', '≈', '≡', '∈', '∉', '⊂', '⊃'],
  ['□', '■', '▢', '▭', '△', '▲', '▽', '▼', '○', '●', '◯', '◇', '◆', '☐', '✓', '✗', '★', '☆'],
  ['→', '←', '↑', '↓', '↔', '↕', '⇒', '⇐', '⇔', '°', '′', '″', '∠', '∡', '⊥', '∥', '≅', '∼'],
  ['฿', '$', '€', '¥', '£', '°C', '°F', 'km', 'm', 'cm', 'mm', 'kg', 'g', 'ml', 'L', '㎡', '㎥'],
] as const;

const GROUP_KEYS = [
  'symbol.group.basic',
  'symbol.group.fractions',
  'symbol.group.powers',
  'symbol.group.roots',
  'symbol.group.shapes',
  'symbol.group.arrows',
  'symbol.group.units',
] as const;

interface Props {
  /** Optional explicit target. If omitted, uses the last-focused text field. */
  targetRef?: React.RefObject<HTMLTextAreaElement | HTMLInputElement>;
  className?: string;
  /** Hide the always-visible quick row and only show the dropdown trigger */
  compact?: boolean;
}

export function SymbolPicker({ targetRef, className, compact = false }: Props) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const lastFocusedRef = useRef<HTMLTextAreaElement | HTMLInputElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onFocusIn(e: FocusEvent) {
      const el = e.target as HTMLElement | null;
      if (!el) return;
      if (el instanceof HTMLTextAreaElement) {
        lastFocusedRef.current = el;
      } else if (el instanceof HTMLInputElement && (el.type === 'text' || el.type === 'search' || el.type === '')) {
        lastFocusedRef.current = el;
      }
    }
    document.addEventListener('focusin', onFocusIn);
    return () => document.removeEventListener('focusin', onFocusIn);
  }, []);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function insert(sym: string) {
    const el = targetRef?.current ?? lastFocusedRef.current;
    if (!el) {
      // Fallback: copy to clipboard so the user can paste manually
      navigator.clipboard?.writeText(sym).catch(() => {});
      return;
    }
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    const next = el.value.slice(0, start) + sym + el.value.slice(end);

    const proto = el instanceof HTMLTextAreaElement
      ? HTMLTextAreaElement.prototype
      : HTMLInputElement.prototype;
    const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
    setter?.call(el, next);
    el.dispatchEvent(new Event('input', { bubbles: true }));

    const pos = start + sym.length;
    el.focus();
    el.setSelectionRange(pos, pos);
  }

  return (
    <div className={cn('relative', className)}>
      <div className="flex flex-wrap items-center gap-1">
        {!compact && QUICK.map((s) => (
          <button
            key={s}
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => insert(s)}
            className="w-8 h-8 inline-flex items-center justify-center rounded-md bg-slate-50 dark:bg-slate-800 hover:bg-brand-50 dark:hover:bg-brand-900/40 text-slate-800 dark:text-slate-100 hover:text-brand-700 dark:hover:text-brand-300 text-base border border-slate-200 dark:border-slate-700 hover:border-brand-300 transition-colors"
            title={`${t('symbol.insert')} ${s}`}
          >
            {s}
          </button>
        ))}
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-1 h-8 px-2.5 text-xs font-medium rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
          aria-label={t('symbol.insert')}
          aria-expanded={open}
        >
          <Sigma className="w-3.5 h-3.5" />
          <span>{t('symbol.more')}</span>
          <ChevronDown className={cn('w-3 h-3 transition-transform', open && 'rotate-180')} />
        </button>
      </div>

      {open && (
        <div
          ref={popoverRef}
          className="absolute z-30 left-0 top-full mt-1.5 w-[380px] max-w-[calc(100vw-2rem)] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl p-3 max-h-[360px] overflow-y-auto"
          onMouseDown={(e) => e.preventDefault()}
        >
          {GROUP_KEYS.map((key, gi) => (
            <div key={key} className="mb-3 last:mb-0">
              <div className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1.5">
                {t(key)}
              </div>
              <div className="grid grid-cols-9 gap-1">
                {GROUP_SYMBOLS[gi].map((s, i) => (
                  <button
                    key={`${key}-${i}`}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => insert(s)}
                    className="h-8 inline-flex items-center justify-center rounded-md bg-slate-50 dark:bg-slate-800 hover:bg-brand-50 dark:hover:bg-brand-900/40 text-slate-800 dark:text-slate-100 hover:text-brand-700 dark:hover:text-brand-300 text-sm border border-slate-100 dark:border-slate-700 hover:border-brand-300 transition-colors"
                    title={`${t('symbol.insert')} ${s}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ))}
          <div className="text-[11px] text-slate-400 dark:text-slate-500 pt-2 mt-1 border-t border-slate-100 dark:border-slate-800">
            {t('symbol.hint')}
          </div>
        </div>
      )}
    </div>
  );
}
